import React, { useState, useEffect } from "react";
import { 
  Box, 
  Drawer, 
  Typography, 
  Button, 
  Divider, 
  Select, 
  MenuItem, 
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import api from "../../api/api";

const FiltersModal = ({ open, onClose, filters, activeFilters, onApplyFilters, onResetFilters, tableKey }) => {
  // Более надежная инициализация фильтров
  const initializeFilters = () => {
    if (!filters || !Array.isArray(filters)) return {};
    return filters.reduce((acc, filter) => {
      // Если есть activeFilters, используем их значения, иначе пустая строка
      acc[filter.filter_name] = activeFilters?.[filter.filter_name] || ""; 
      return acc;
    }, {});
  };

  const [filterValues, setFilterValues] = useState(initializeFilters);
  const [savedFilters, setSavedFilters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  // Обновляем фильтры при изменении пропсов
  useEffect(() => {
    if (filters && Array.isArray(filters)) {
      setFilterValues(initializeFilters());
    }
  }, [filters, activeFilters]);

  // Загружаем сохраненные фильтры при открытии модального окна
  useEffect(() => {
    if (open && tableKey) {
      loadSavedFilters();
    }
  }, [open, tableKey]);

  // Загрузка сохраненных фильтров пользователя
  const loadSavedFilters = async () => {
    try {
      setLoading(true);
      
      const response = await api.get(`/filters/${tableKey}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setSavedFilters(response.data);
      
      // Восстанавливаем значения фильтров из сохраненных данных
      const savedValues = {};
      response.data.forEach(filter => {
        if (filter.filter_type === "value" && filter.options && filter.options.length > 0) {
          const value = filter.options[0] || "";
          
          // Найдем конфигурацию фильтра чтобы определить его тип
          const filterConfig = filters.find(f => f.filter_name === filter.filter_name);
          
          // Обработка checkbox фильтров
          if (filterConfig && filterConfig.filter_type === "checkbox") {
            const boolValue = value === true || value === "true" || value === 1 || value === "1";
            savedValues[filter.filter_name] = boolValue;
          }
          // Обработка обычных фильтров
          else {
            savedValues[filter.filter_name] = value;
          }
        }
      });
      
      // Применяем сохраненные значения, даже если некоторые пустые
      if (Object.keys(savedValues).length > 0) {
        setFilterValues(prev => ({...prev, ...savedValues}));
      }
      
    } catch (error) {
      console.error("❌ Ошибка загрузки сохраненных фильтров:", error);
    } finally {
      setLoading(false);
    }
  };

  // Сохранение конфигурации фильтров
  const saveFilterConfig = async (filterName, filterConfig) => {
    try {
      const filterData = {
        table_key: tableKey,
        filter_name: filterName,
        filter_type: filterConfig.filter_type,
        options: filterConfig.options || [],
        is_visible: true
      };

      await api.post("/filters/", filterData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      setSaveStatus({ type: "success", message: "Фильтр сохранен!" });
      setTimeout(() => setSaveStatus(null), 3000);
      
      // Перезагружаем сохраненные фильтры
      loadSavedFilters();
    } catch (error) {
      console.error("Ошибка сохранения фильтра:", error);
      setSaveStatus({ type: "error", message: "Ошибка сохранения фильтра" });
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  // Сохранение всех фильтров в filter_config
  const saveAllFilterValues = async (values) => {
    try {
      // Сохраняем каждый фильтр отдельно в таблицу filter_config
      for (const [filterName, filterValue] of Object.entries(values)) {
        // Обрабатываем boolean и строковые значения по-разному
        let valueToSave;
        if (typeof filterValue === "boolean") {
          valueToSave = filterValue.toString(); // Преобразуем boolean в строку
        } else {
          valueToSave = filterValue && filterValue.trim() !== "" ? filterValue : "";
        }
        
        const response = await api.post("/filters/", {
          table_key: tableKey,
          filter_name: filterName,
          filter_type: "value", // Указываем, что это сохраненное значение
          options: [valueToSave], // Сохраняем значение в массиве options (даже если пустое)
          is_visible: true
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        });
      }
      
      setSaveStatus({ type: "success", message: "Фильтры сохранены!" });
      setTimeout(() => setSaveStatus(null), 3000);
      return true;
      
    } catch (error) {
      console.error("❌ Ошибка сохранения значений фильтров:", error);
      setSaveStatus({ type: "error", message: "Ошибка сохранения фильтров" });
      setTimeout(() => setSaveStatus(null), 3000);
      return false;
    }
  };

  // Обработчик изменения значения фильтра
  const handleFilterChange = (filterName, value) => {
    setFilterValues((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  // Применение фильтров
  const handleApplyFilters = async () => {
    // Сохраняем значения всех фильтров в filter_config
    if (!await saveAllFilterValues(filterValues)) return;
    
    if (onApplyFilters) {
      onApplyFilters(filterValues);
    }
    onClose(); // Закрыть окно после применения
  };

  // Сброс фильтров
  const handleResetFilters = async () => {
    const resetValues = Object.fromEntries((filters || []).map(filter => [
      filter.filter_name, filter.filter_type === "checkbox" ? false : ""
    ]));
    
    // Сохраняем пустые значения в базу данных
    if (!await saveAllFilterValues(resetValues)) return;
    setFilterValues(resetValues);
    
    if (onResetFilters) {
      onResetFilters();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: "400px", // Ширина окна
          padding: "20px",
          boxSizing: "border-box",
          maxHeight: "100%", // Окно тянется вниз
        },
      }}
    >
      {/* Заголовок */}
      <Typography variant="h6" component="div" gutterBottom>
        Фильтры
      </Typography>
      
      {/* Статус сохранения */}
      {saveStatus && (
        <Alert 
          severity={saveStatus.type} 
          sx={{ mb: 2 }}
          onClose={() => setSaveStatus(null)}
        >
          {saveStatus.message}
        </Alert>
      )}
      
      <Divider />
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto", // Прокрутка для контента
            marginTop: "20px",
          }}
        >
          {filters && Array.isArray(filters) && filters.map((filter) => (
            <Box key={`${filter.table_key}_${filter.filter_name}`} sx={{ marginBottom: "20px" }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1">
                  {filter.filter_label || filter.filter_name} {/* Используем русское название если есть */}
                </Typography>
                {/* Кнопка сохранения конфигурации фильтра */}
                <IconButton
                  size="small"
                  onClick={() => saveFilterConfig(filter.filter_name, filter)}
                  title="Сохранить конфигурацию фильтра"
                >
                  <SaveIcon fontSize="small" />
                </IconButton>
              </Box>
              {filter.filter_type === "select" && (
                <Select
                  value={filterValues[filter.filter_name] || ""} // Гарантируем, что значение не undefined
                  onChange={(e) => handleFilterChange(filter.filter_name, e.target.value)}
                  fullWidth
                >
                  <MenuItem value="">
                    <em>Не выбрано</em>
                  </MenuItem>
                  {filter.options && Array.isArray(filter.options) && filter.options.map((option, index) => (
                    <MenuItem key={index} value={option?.value || option || ""}>
                      {option?.label || option || ""}
                    </MenuItem>
                  ))}
                </Select>
              )}
              {filter.filter_type === "date" && (
                <TextField
                  type="date"
                  value={filterValues[filter.filter_name] || ""} // Гарантируем, что значение не undefined
                  onChange={(e) => handleFilterChange(filter.filter_name, e.target.value)}
                  fullWidth
                />
              )}
              {filter.filter_type === "checkbox" && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filterValues[filter.filter_name] === true || filterValues[filter.filter_name] === "true"}
                      onChange={(e) => handleFilterChange(filter.filter_name, e.target.checked)}
                    />
                  }
                  label={filter.filter_label || filter.filter_name}
                />
              )}
            </Box>
          ))}
        </Box>
      )}
      {/* Кнопки действий */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "20px",
        }}
      >
        <Button onClick={onClose}>Закрыть</Button>
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button onClick={handleResetFilters}>
            Сбросить
          </Button>
          <Button variant="contained" onClick={handleApplyFilters}>
            Применить
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default FiltersModal;
